import { getApiBaseUrl } from '@/config/api'
import { getTenantSlug } from '@/config/tenant'
import { getClienteBearerToken } from '@/utils/clienteAuth'

const MSG_SERVIDOR_INDISPONIVEL =
  'Não foi possível contactar o servidor. Verifique se o servidor está a correr (npm run dev:server ou npm run dev:all).'

const MSG_SERVIDOR_NATIVO =
  'Não foi possível contactar o servidor. Verifique a ligação à Internet e tente novamente.'

const DEFAULT_TIMEOUT_MS = 15000

function servidorIndisponivelMsg(suffix = ''): string {
  const base =
    typeof window !== 'undefined' && window.__NATIVE_APP__ === true
      ? MSG_SERVIDOR_NATIVO
      : MSG_SERVIDOR_INDISPONIVEL
  return suffix ? `${base} ${suffix}` : base
}

function isErroRede(err: unknown): boolean {
  if (err instanceof TypeError && err.message === 'Failed to fetch') return true
  if (err instanceof Error && err.message.includes('Failed to fetch')) return true
  return false
}

export const apiBase = (path: string): string => {
  const base = getApiBaseUrl()
  return base ? `${base}${path}` : path
}

export interface FetchApiOptions extends RequestInit {
  timeoutMs?: number
}

/**
 * Wrapper de fetch com:
 * - montagem automática da URL base
 * - tratamento de erro de rede / servidor offline
 * - timeout configurável
 */
export async function fetchApi(path: string, options: FetchApiOptions = {}): Promise<Response> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal, headers: inputHeaders, ...rest } = options

  const headers = new Headers(inputHeaders ?? undefined)
  const slug = getTenantSlug()
  if (slug && !headers.has('X-Tenant-Slug')) {
    headers.set('X-Tenant-Slug', slug)
  }
  const bearer = getClienteBearerToken()
  if (bearer && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${bearer}`)
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  // Combina signal externo (se existir) com o do timeout
  const combinedSignal = signal
    ? new AbortController()
    : controller

  if (signal && combinedSignal !== controller) {
    signal.addEventListener('abort', () => combinedSignal.abort(), { once: true })
  }

  const url = path.startsWith('http') ? path : apiBase(path)

  try {
    return await fetch(url, {
      credentials: 'include',
      ...rest,
      headers,
      signal: combinedSignal.signal ?? controller.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(servidorIndisponivelMsg('(timeout)'))
    }
    if (isErroRede(err)) throw new Error(servidorIndisponivelMsg())
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Lê corpo de erro (JSON/string) e lança Error com mensagem amigável.
 */
export async function parseError(res: Response, text: string): Promise<never> {
  const data = text
    ? (() => {
        try {
          return JSON.parse(text)
        } catch {
          return { error: text }
        }
      })()
    : { error: res.statusText }

  const msg =
    typeof data === 'object' && data !== null && 'error' in data
      ? String((data as { error: string }).error)
      : String(data)

  throw new Error(msg || `Erro na API (${res.status})`)
}

/**
 * Trata respostas JSON "normais" (clientes, app).
 * - converte HTML em mensagem de servidor offline / má configuração
 */
export async function handleJson<T>(res: Response): Promise<T> {
  const text = await res.text()

  if (!res.ok) return parseError(res, text)

  if (res.status === 204 || !text) return undefined as T

  const trimmed = text.trim()

  // Se voltou HTML, provavelmente o servidor não está a responder a JSON (ex.: erro de proxy/Vite)
  if (trimmed.startsWith('<')) {
    throw new Error(
      'A API não respondeu em JSON. 1) Inicie o servidor: npm run dev:server (ou npm run dev:all). 2) Reinicie o frontend para recarregar o .env.'
    )
  }

  try {
    return JSON.parse(text) as T
  } catch {
    throw new Error('Resposta da API não é JSON válido.')
  }
}

